import React, { useState, useEffect } from 'react';
import { AmenityGrid, NearbyPlaceGrid, MatchCard } from './listingFeatures';
import Icon3D from './Icon3D';
import { useAuth } from './AuthContext';
import { publicSearchApi, S3_BASE_URL } from './api';
import { openPrimeModal } from './PrimeModal';

const IconMessage = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
);

const IconMapPinSolid = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
    </svg>
);

const IconUsers = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const IconTool = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
    </svg>
);

const IconEye = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);

const IconArrowLeft = ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
);

const IconPhone = ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
);

const IconLock = ({ size = 18, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
);

const IconSparkles = ({ size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
);

const IconUserPlus = ({ size = 28, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></svg>
);

const S3Image = ({ s3Key, style, alt }) => {
    const [url, setUrl] = useState('');

    useEffect(() => {
        if (!s3Key) return;
        if (s3Key.startsWith('http://') || s3Key.startsWith('https://') || s3Key.startsWith('data:')) {
            setUrl(s3Key);
        } else {
            const cleanKey = s3Key.startsWith('/') ? s3Key.substring(1) : s3Key;
            setUrl(`${S3_BASE_URL}${cleanKey}`);
        }
    }, [s3Key]);

    if (!url) {
        return (
            <div style={{ ...style, background: '#DDE3DC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#9CA3AF', fontSize: '10px' }}>Loading...</span>
            </div>
        );
    }
    return <img src={url} alt={alt} style={style} />;
};

const DetailsPage = ({ onRequireLogin }) => {
    const { tokens, isSubscribed } = useAuth();
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const fetchDetails = async () => {
            const searchParams = new URLSearchParams(window.location.search);
            const typeParam = searchParams.get('type');
            const idParam = searchParams.get('id');

            if (!typeParam || !idParam) {
                setLoadingDetails(false);
                return;
            }

            // If not logged in, skip API call — the login prompt will show
            if (!tokens?.access) {
                setLoadingDetails(false);
                return;
            }

            try {
                let res;
                if (typeParam === 'listing') {
                    // Use the public detail endpoint, not /flat/listings/{id}: it is the
                    // one that resolves owner info and applies the contact paywall.
                    res = await publicSearchApi.getListingDetail(idParam, tokens.access);
                } else {
                    const lat = searchParams.get('lat') || 0;
                    const lng = searchParams.get('lng') || 0;
                    res = await publicSearchApi.getRequirementDetail(idParam, { lat, lng }, tokens.access);
                }
                setSelectedItem({ type: typeParam, id: idParam, data: res });
            } catch (err) {
                console.error('Failed to load item detail', err);
            } finally {
                setLoadingDetails(false);
            }
        };

        fetchDetails();
    }, [tokens]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (fullscreenImage !== null && selectedItem?.data?.images?.length > 0) {
                if (e.key === 'ArrowRight') {
                    setFullscreenImage(prev => prev < selectedItem.data.images.length - 1 ? prev + 1 : 0);
                } else if (e.key === 'ArrowLeft') {
                    setFullscreenImage(prev => prev > 0 ? prev - 1 : selectedItem.data.images.length - 1);
                } else if (e.key === 'Escape') {
                    setFullscreenImage(null);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [fullscreenImage, selectedItem]);

    if (loadingDetails) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)' }}>
                <div style={{ textAlign: 'center', color: 'white' }}>
                    <div style={{ width: '56px', height: '56px', border: '4px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <p style={{ fontSize: '16px', fontWeight: '600', opacity: 0.85 }}>Loading property details...</p>
                </div>
            </div>
        );
    }

    if (!tokens || !tokens.access) {
        return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div style={{ background: 'white', padding: '40px', borderRadius: '28px', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.15)', maxWidth: '400px', width: '100%' }}>
                    <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                        <IconMapPinSolid size={36} color="#2563EB" />
                    </div>
                    <h2 style={{ fontSize: '24px', fontWeight: '800', margin: '0 0 12px', color: '#111827' }}>Login Required</h2>
                    <p style={{ color: '#4B5563', fontSize: '15px', marginBottom: '28px', lineHeight: '1.6' }}>You must be verified to view full property details and owner contact info.</p>
                    <button onClick={() => onRequireLogin()} style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px 32px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', width: '100%', boxShadow: '0 8px 24px rgba(34,106,70,0.3)', marginBottom: '12px' }}>Sign In Now</button>
                    <button onClick={() => window.close()} style={{ background: 'transparent', color: '#6B7280', border: 'none', padding: '10px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', width: '100%' }}>Close Tab</button>
                </div>
            </div>
        );
    }

    if (!selectedItem || !selectedItem.data) {
        return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(155deg, #1E40AF, #2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: 'white', padding: '40px', borderRadius: '24px', textAlign: 'center', maxWidth: '400px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                    <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#111827', marginBottom: '8px' }}>Failed to Load</h2>
                    <p style={{ color: '#6B7280', fontSize: '15px' }}>We couldn't fetch this property. Please close the tab and try again.</p>
                </div>
            </div>
        );
    }

    // The server is the authority on all three: it only sends phoneNumber when the
    // viewer is entitled to it. These flags just pick the right words and actions.
    const canSeeContact = !!(selectedItem.data.isPhoneVisible && selectedItem.data.phoneNumber);
    const lockedForPrime = selectedItem.data.contactLockReason === 'PRIME_REQUIRED' || !isSubscribed;

    const handleCall = () => {
        if (!tokens?.access) return onRequireLogin();
        if (canSeeContact) {
            window.location.href = `tel:${selectedItem.data.phoneNumber}`;
            return;
        }
        if (selectedItem.data.contactLockReason === 'OWNER_HIDDEN') {
            // Nothing to sell here — the owner opted out of showing their number.
            window.alert('This user has chosen to hide their number. Try chatting instead.');
            return;
        }
        openPrimeModal('See this contact number and start chatting right away.');
    };

    const handleChat = () => {
        if (!tokens?.access) return onRequireLogin();
        if (!isSubscribed) {
            return openPrimeModal('Chat with owners and flatmates without limits.');
        }
        window.dispatchEvent(new CustomEvent('openChat', {
            detail: {
                receiverUserId: selectedItem.data.ownerId || '',
                listingId: String(selectedItem.id),
                receiverName: selectedItem.data.ownerName || selectedItem.data.profile?.name,
                receiverPic: selectedItem.data.ownerProfilePic || selectedItem.data.profile?.profilePic,
            },
        }));
    };

    return (
        <div className="details-page-wrapper">
            {/* Blue gradient header */}
            <header style={{
                background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)',
                padding: '100px 20px 32px',
                position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '280px', height: '280px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <button onClick={() => window.close()} style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', width: '44px', height: '44px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                        <IconArrowLeft size={22} color="white" />
                    </button>
                    <div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Property Details</div>
                        <h1 style={{ fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: '800', color: 'white', margin: 0, lineHeight: '1.2' }}>
                            {selectedItem.data.location?.name || 'Property Listing'}
                        </h1>
                    </div>
                </div>
            </header>

            <div className="details-page-container">

                <div className="details-grid">
                    {/* LEFT COLUMN - MAIN CONTENT */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                        {/* HERO IMAGE OR BUDGET BLOCK */}
                        {selectedItem.type === 'requirement' ? (
                            <div style={{ width: '100%', padding: isMobile ? '32px 24px' : '48px 40px', background: 'linear-gradient(135deg, #115E59 0%, #0F766E 100%)', borderRadius: isMobile ? '20px' : '32px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: isMobile ? '14px' : '18px', opacity: 0.9, marginBottom: '8px', letterSpacing: '0.5px' }}>Budget</div>
                                <div style={{ fontSize: isMobile ? '28px' : '56px', fontWeight: '800', lineHeight: '1.2' }}>
                                    ₹{selectedItem.data.budgetMin?.toLocaleString() || 0} - ₹{selectedItem.data.budgetMax?.toLocaleString() || 0}
                                </div>
                            </div>
                        ) : (
                            <div style={{ position: 'relative', width: '100%', height: isMobile ? '240px' : '340px', backgroundColor: '#DDE3DC', borderRadius: isMobile ? '20px' : '28px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
                                <div style={{ display: 'flex', width: '100%', height: '100%', overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    <style>{`
                                        ::-webkit-scrollbar { display: none; }
                                    `}</style>
                                    {selectedItem.data.images && selectedItem.data.images.length > 0 ? (
                                        selectedItem.data.images.map((imgKey, index) => (
                                            <div key={index} style={{ flex: '0 0 auto', width: '100%', height: '100%', position: 'relative', scrollSnapAlign: 'start', cursor: 'pointer' }} onClick={() => setFullscreenImage(index)}>
                                                <S3Image s3Key={imgKey} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.85) 100%)' }} />

                                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(0,0,0,0.5)', padding: '10px 20px', borderRadius: '24px', display: 'flex', alignItems: 'center', gap: '8px', backdropFilter: 'blur(8px)', color: 'white', fontSize: '13px', fontWeight: '700', opacity: 0.95 }}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>
                                                    Tap to view full image
                                                </div>

                                                {selectedItem.data.images.length > 1 && (
                                                    <div style={{ position: 'absolute', bottom: '80px', left: '0', right: '0', display: 'flex', justifyContent: 'center', gap: '6px' }}>
                                                        {selectedItem.data.images.map((_, dotIndex) => (
                                                            <div key={dotIndex} style={{ width: index === dotIndex ? '20px' : '6px', height: '6px', borderRadius: '3px', background: 'white', opacity: index === dotIndex ? 1 : 0.4, transition: 'all 0.3s ease' }}></div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ flex: '0 0 auto', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconMapPinSolid size={48} color="#9CA3AF" /></div>
                                    )}
                                </div>
                                <div style={{ position: 'absolute', top: '24px', right: '24px', pointerEvents: 'none' }}>
                                    {selectedItem.data.postType && <span style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', color: '#111827', padding: '10px 24px', borderRadius: '24px', fontSize: '14px', fontWeight: '800', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>{selectedItem.data.postType}</span>}
                                </div>
                                <div style={{ position: 'absolute', bottom: '24px', left: '32px', right: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pointerEvents: 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', color: 'white' }}>
                                        <span style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: '800', lineHeight: 1 }}>₹{(selectedItem.data.rent || selectedItem.data.budgetMax || 0).toLocaleString()}</span>
                                        <span style={{ fontSize: isMobile ? '13px' : '16px', fontWeight: '500', opacity: 0.9 }}>/month</span>
                                    </div>
                                    {selectedItem.data.negotiable && (
                                        <span style={{ background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(12px)', color: 'white', padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>Negotiable</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* CONTENT CONTAINER */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            {/* Title & Stats */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <div style={{ marginTop: '2px' }}><IconMapPinSolid size={32} color="#10B981" /></div>
                                <div>
                                    <h3 style={{ fontSize: isMobile ? '22px' : '32px', fontWeight: '800', margin: '0 0 16px 0', color: '#111827', lineHeight: '1.2' }}>{selectedItem.data.location?.name || 'Any Location Nearby'}</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                        <span style={{ background: '#E4EFC0', color: '#1E40AF', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1E40AF' }}></div> ACTIVE
                                        </span>
                                        <span style={{ color: '#6B7280', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}><IconEye size={18} /> {selectedItem.data.viewCount || 5} views</span>
                                        <span style={{ color: '#6B7280', fontSize: '15px', fontWeight: '600' }}>Today</span>
                                    </div>
                                </div>
                            </div>

                            {/* Traits.

                                One compact row rather than four tall tiles. At
                                minmax(180px, 1fr) the fourth card wrapped to its
                                own line on a desktop column, leaving a hole beside
                                it, and each tile stacked a 32px icon over two lines
                                of text inside 24px of padding — roughly 240px tall
                                to convey one word. Icon beside the text, fixed four
                                columns, and the set reads in a glance. */}
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '10px' : '12px' }}>
                                {[
                                    {
                                        label: 'Type',
                                        tint: '#D1FAE5',
                                        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
                                        value: selectedItem.data.postType === 'ANY' ? 'Flat/PG' : (selectedItem.data.postType === 'ROOM' ? 'Room' : (selectedItem.data.postType === 'FLAT' ? 'Flat' : selectedItem.data.postType)),
                                    },
                                    {
                                        label: 'For',
                                        tint: '#FFEDD5',
                                        icon: <IconUsers size={20} color="#EA580C" />,
                                        value: selectedItem.data.lookingGender === 'ANY' || selectedItem.data.gender === 'ANY' ? 'Any' : (selectedItem.data.lookingGender || selectedItem.data.gender || 'Any'),
                                    },
                                    {
                                        label: 'Occupancy',
                                        tint: '#EDE9FE',
                                        icon: <IconUsers size={20} color="#8B5CF6" />,
                                        value: selectedItem.data.occupany && selectedItem.data.occupany !== 'ANY' ? selectedItem.data.occupany.charAt(0) + selectedItem.data.occupany.slice(1).toLowerCase() : 'Single',
                                    },
                                    {
                                        label: 'Furnishing',
                                        tint: '#CFFAFE',
                                        icon: <IconTool size={20} color="#0891B2" />,
                                        value: selectedItem.data.furnishing === 'FURNISHED' ? 'Furnished' : (selectedItem.data.furnishing === 'SEMI' ? 'Semi-Furnished' : 'Unfurnished'),
                                    },
                                ].map((t) => (
                                    <div
                                        key={t.label}
                                        style={{
                                            border: '1px solid #E7EBE6',
                                            borderRadius: '16px',
                                            padding: isMobile ? '12px' : '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '11px',
                                            background: 'white',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                                        }}
                                    >
                                        <span style={{ width: '38px', height: '38px', borderRadius: '12px', background: t.tint, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {t.icon}
                                        </span>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', lineHeight: 1.2 }}>{t.label}</div>
                                            <div style={{ fontWeight: '800', fontSize: isMobile ? '14px' : '15px', color: '#111827', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Flat Details */}
                            {(selectedItem.data.isFlat || selectedItem.data.flat) && (
                                <div style={{ background: 'white', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #E5E9E3' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '800', margin: '0 0 24px 0', color: '#111827' }}>
                                        🏠 Flat Details
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '24px' }}>
                                        <div>
                                            <div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>BHK</div>
                                            <div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>{selectedItem.data.flat?.bhk || selectedItem.data.bhk || 1}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>Floor</div>
                                            <div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>{selectedItem.data.flat?.floor || 1} / {selectedItem.data.flat?.totalFloors || 2}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>Area</div>
                                            <div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>{selectedItem.data.flat?.areaSqft || 1200} sqft</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* PG Pricing Details */}
                            {(selectedItem.data.isPG || selectedItem.data.payingGuest) && (
                                <div style={{ background: 'white', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #E5E9E3' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '800', margin: '0 0 24px 0', color: '#111827' }}>
                                        🏢 PG Pricing Details
                                    </h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '24px' }}>
                                        {selectedItem.data.payingGuest?.singleSharingPrice > 0 && <div><div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>Single</div><div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>₹{selectedItem.data.payingGuest.singleSharingPrice}</div></div>}
                                        {selectedItem.data.payingGuest?.doubleSharingPrice > 0 && <div><div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>Double</div><div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>₹{selectedItem.data.payingGuest.doubleSharingPrice}</div></div>}
                                        {selectedItem.data.payingGuest?.tripleSharingPrice > 0 && <div><div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>Triple</div><div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>₹{selectedItem.data.payingGuest.tripleSharingPrice}</div></div>}
                                        {selectedItem.data.payingGuest?.depositeMonth > 0 && <div><div style={{ color: '#6B7280', fontSize: '15px', marginBottom: '8px', fontWeight: '600' }}>Deposit</div><div style={{ fontWeight: '800', fontSize: '20px', color: '#111827' }}>{selectedItem.data.payingGuest.depositeMonth} Months</div></div>}
                                    </div>
                                </div>
                            )}

                            {/* Amenities */}
                            {selectedItem.data.amenties && selectedItem.data.amenties.length > 0 && (
                                <div>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '800', margin: '0 0 20px 0', color: '#111827' }}>
                                        <IconSparkles size={24} color="#EAB308" /> Amenities
                                    </h4>
                                    <AmenityGrid amenities={selectedItem.data.amenties} />
                                </div>
                            )}

                            {/* What's around — the API sends these with walking
                                distances; each tile opens the place in Maps. */}
                            {selectedItem.data.nearbyPlaces?.length > 0 && (
                                <div>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '800', margin: '0 0 20px 0', color: '#111827' }}>
                                        <Icon3D name="location" size={26} /> What's nearby
                                    </h4>
                                    <NearbyPlaceGrid places={selectedItem.data.nearbyPlaces} />
                                </div>
                            )}

                            <MatchCard
                                score={selectedItem.data.lifestyleMatchScore ?? selectedItem.data.lifestyleMatch?.score}
                                match={selectedItem.data.lifestyleMatch}
                            />

                            {/* Description */}
                            {selectedItem.data.description && (
                                <div style={{ background: 'white', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #E5E9E3' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '800', margin: '0 0 20px 0', color: '#111827' }}>
                                        📝 Description
                                    </h4>
                                    <p style={{ color: '#4B5563', lineHeight: '1.8', margin: 0, fontSize: '16px', whiteSpace: 'pre-wrap' }}>
                                        {selectedItem.data.description}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN - SIDEBAR */}
                    <div className="details-sidebar">
                        {/* PRICING & ACTION CARD */}
                        <div style={{ background: 'white', borderRadius: '28px', padding: '28px', boxShadow: '0 8px 32px rgba(34,106,70,0.08)', border: '1px solid #E4EFC0' }}>
                            {selectedItem.type !== 'requirement' && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' }}>
                                    <div>
                                        <div style={{ color: '#6B7280', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Rent / Budget</div>
                                        <div style={{ fontSize: '32px', fontWeight: '800', color: '#111827', display: 'flex', alignItems: 'baseline', gap: '6px', lineHeight: '1.2' }}>
                                            ₹{(selectedItem.data.rent || selectedItem.data.budgetMax || 0).toLocaleString()} <span style={{ fontSize: '16px', fontWeight: '600', color: '#6B7280' }}>/mo</span>
                                        </div>
                                    </div>
                                    {selectedItem.data.negotiable && (
                                        <span style={{ background: '#FEF3C7', color: '#D97706', padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '800' }}>Negotiable</span>
                                    )}
                                </div>
                            )}

                            {/* Prime upsell — shown whenever the server withheld the number
                                because the viewer hasn't subscribed. */}
                            {!canSeeContact && lockedForPrime && (
                                <div style={{ background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)', border: '1px solid #BFDBFE', borderRadius: '20px', padding: '18px 20px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: '#2563EB', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <IconLock size={20} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, color: '#1E3A8A', fontSize: '15px' }}>Contact hidden</div>
                                        <div style={{ color: '#1D4ED8', fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>
                                            Get FlatLoop Prime to call and chat
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: selectedItem.type === 'requirement' ? 'row' : 'column', gap: '14px' }}>
                                <button onClick={handleCall}
                                    style={{ flex: 1, border: '2px solid #2563EB', color: '#2563EB', background: 'white', borderRadius: '18px', padding: '15px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '15px', transition: '0.2s' }}
                                    onMouseOver={e => { e.currentTarget.style.background = '#EFF6FF'; }}
                                    onMouseOut={e => { e.currentTarget.style.background = 'white'; }}>
                                    {canSeeContact ? <IconPhone size={18} /> : <IconLock size={18} />}
                                    {canSeeContact
                                        ? (selectedItem.type === 'requirement' ? 'Call' : 'Call Owner')
                                        : 'Unlock Number'}
                                </button>
                                <button onClick={handleChat}
                                    style={{ flex: selectedItem.type === 'requirement' ? 2 : 1, background: 'linear-gradient(135deg, #1E40AF, #2563EB)', color: 'white', border: 'none', borderRadius: '18px', padding: '15px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '15px', transition: '0.2s', boxShadow: '0 8px 24px rgba(34,106,70,0.3)' }}
                                    onMouseOver={e => e.currentTarget.style.opacity = '0.9'}
                                    onMouseOut={e => e.currentTarget.style.opacity = '1'}>
                                    <IconMessage /> {selectedItem.type === 'requirement' ? 'Chat' : 'Chat with Owner'}
                                </button>
                            </div>
                        </div>

                        {/* OWNER CARD */}
                        <div style={{ background: 'white', borderRadius: '32px', padding: '32px', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', border: '1px solid #DDE3DC' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: selectedItem.data.ownerPreferences && selectedItem.data.ownerPreferences.length > 0 ? '24px' : '0', borderBottom: selectedItem.data.ownerPreferences && selectedItem.data.ownerPreferences.length > 0 ? '1px solid #E5E9E3' : 'none', paddingBottom: selectedItem.data.ownerPreferences && selectedItem.data.ownerPreferences.length > 0 ? '24px' : '0' }}>
                                {selectedItem.data.ownerProfilePic || selectedItem.data.profile?.profilePic ? (
                                    <img src={`${S3_BASE_URL}${selectedItem.data.ownerProfilePic || selectedItem.data.profile?.profilePic}`} alt="Owner" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconUserPlus size={40} color="white" /></div>
                                )}
                                <div>
                                    <h3 style={{ fontSize: '20px', fontWeight: '800', margin: '0 0 6px 0', color: '#111827' }}>{selectedItem.data.ownerName || selectedItem.data.profile?.name || 'Verified User'}</h3>
                                    <div style={{ color: '#6B7280', fontSize: '15px', fontWeight: '600' }}>{selectedItem.type === 'requirement' ? 'Looking for Property' : 'Property Owner'}</div>
                                    {canSeeContact ? (
                                        <div style={{ color: '#047857', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontWeight: '800' }}>
                                            <IconPhone size={16} /> {selectedItem.data.phoneNumber}
                                        </div>
                                    ) : lockedForPrime ? (
                                        <button
                                            onClick={handleCall}
                                            style={{ marginTop: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8', borderRadius: '12px', padding: '7px 12px', fontSize: '13px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                                        >
                                            <IconLock size={14} /> Number hidden · Unlock
                                        </button>
                                    ) : (
                                        <div style={{ color: '#9CA3AF', fontSize: '13px', marginTop: '8px', fontWeight: 700 }}>
                                            Number hidden by user
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedItem.data.ownerPreferences && selectedItem.data.ownerPreferences.length > 0 && (
                                <div>
                                    <div style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '1px', color: '#6B7280', textTransform: 'uppercase', marginBottom: '16px' }}>LIFESTYLE</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                        {selectedItem.data.ownerPreferences.map(pref => (
                                            <span key={pref} style={{ background: '#F3F4F6', padding: '10px 18px', borderRadius: '20px', fontSize: '14px', fontWeight: '700', color: '#1F2937', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #DDE3DC' }}>
                                                {pref}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div> {/* details-page-container */}

            {/* FULLSCREEN IMAGE MODAL */}
            {
                fullscreenImage !== null && selectedItem.data.images && selectedItem.data.images.length > 0 && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
                        <button onClick={() => setFullscreenImage(null)} style={{ position: 'absolute', top: '24px', right: '24px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s', zIndex: 10000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '80vh', position: 'relative' }}>
                            <button onClick={(e) => { e.stopPropagation(); setFullscreenImage(prev => prev > 0 ? prev - 1 : selectedItem.data.images.length - 1) }} style={{ position: 'absolute', left: '24px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                                <IconArrowLeft size={24} />
                            </button>

                            <S3Image s3Key={selectedItem.data.images[fullscreenImage]} style={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '12px' }} />

                            <button onClick={(e) => { e.stopPropagation(); setFullscreenImage(prev => prev < selectedItem.data.images.length - 1 ? prev + 1 : 0) }} style={{ position: 'absolute', right: '24px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '48px', height: '48px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(180deg)' }}><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                            </button>
                        </div>

                        <div style={{ marginTop: '24px', color: 'white', fontSize: '16px', fontWeight: '800', background: 'rgba(255,255,255,0.15)', padding: '8px 20px', borderRadius: '24px', letterSpacing: '2px' }}>
                            {fullscreenImage + 1} / {selectedItem.data.images.length}
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default DetailsPage;
